// Lazy view loader.
//
// Each view module is imported only when the user first clicks its
// sidebar entry. Vite/Rollup splits each into its own chunk →
// initial admin bundle stays small; per-view chunks are fetched
// on-demand and cached by the browser thereafter.
//
// Used by main.ts:openView() — looks up modKey, lazy-loads if not
// already registered on window.IFI.studio.<modKey>, then dispatches
// init().

import type { SupaWrapper } from './supabase-client';
import type { AuthApi } from './auth';

export interface ViewModule {
  init(): Promise<void> | void;
}

type ViewFactory = (supa: SupaWrapper, auth: AuthApi) => ViewModule;

/**
 * Lazy-import map: modKey → async function returning the factory.
 * Each entry uses a static import() string so Vite can statically
 * analyze + chunk-split the file at build time.
 */
const LAZY_VIEW_LOADERS: Record<string, () => Promise<ViewFactory>> = {
  // ---- Phase 1 / 2.x core ----
  adminUsers: async () => (await import('./views/admin-users')).createAdminUsersView,
  rawDataManager: async () => (await import('./views/raw-data-manager')).createRawDataManagerView,
  cviAikenPanel: async () => (await import('./views/cvi-aiken-panel')).createCviAikenPanelView,
  triangulationMatrix: async () => (await import('./views/triangulation-matrix')).createTriangulationMatrixView,
  indicatorRegistry: async () => (await import('./views/indicator-registry')).createIndicatorRegistryView,
  bwmStudio: async () => (await import('./views/bwm-studio')).createBwmStudioView,
  calcStudio: async () => (await import('./views/calc-studio')).createCalcStudioView,
  interviewForm: async () => (await import('./views/interview-form')).createInterviewFormView,
  fgdForm: async () => (await import('./views/fgd-form')).createFgdFormView,
  fieldForm: async () => (await import('./views/field-form')).createFieldFormView,
  docForm: async () => (await import('./views/doc-form')).createDocFormView,
  panduan: async () => (await import('./views/panduan')).createPanduanView,
  researchOverview: async () => (await import('./views/research-overview')).createResearchOverviewView,
  auditLogViewer: async () => (await import('./views/audit-log-viewer')).createAuditLogViewerView,
  rawAssetDatabase: async () => (await import('./views/raw-asset-database')).createRawAssetDatabaseView,
  thematicCoding: async () => (await import('./views/thematic-coding')).createThematicCodingView,
  researchWorkflowTracker: async () => (await import('./views/research-workflow-tracker')).createResearchWorkflowTrackerView,
  quality: async () => (await import('./views/quality')).createQualityView,
  exportCenter: async () => (await import('./views/export-center')).createExportCenterView,
  settings: async () => (await import('./views/settings')).createSettingsView,
  // ---- Phase E.2a placeholder/orientation ----
  methodologyExplainer: async () => (await import('./views/methodology-explainer')).createMethodologyExplainerView,
  literatureReview: async () => (await import('./views/literature-review')).createLiteratureReviewView,
  dataCollectionHub: async () => (await import('./views/data-collection-hub')).createDataCollectionHubView,
  policyInsight: async () => (await import('./views/policy-insight')).createPolicyInsightView,
  assetCaseReview: async () => (await import('./views/asset-case-review')).createAssetCaseReviewView,
  pocLocations: async () => (await import('./views/poc-locations')).createPocLocationsView,
  // ---- Phase F #1 assignments ----
  assignmentsAdmin: async () => (await import('./views/assignments-admin')).createAssignmentsAdminView,
};

/**
 * Ensure a view is loaded + registered on window.IFI.studio.<modKey>.
 * Idempotent — returns immediately if already registered.
 * Returns true on success, false if modKey is unknown.
 */
export async function ensureViewLoaded(modKey: string, supa: SupaWrapper, auth: AuthApi): Promise<boolean> {
  const studio = (window.IFI?.studio ?? {}) as Record<string, unknown>;
  if (studio[modKey] && typeof (studio[modKey] as ViewModule).init === 'function') {
    return true;
  }
  const loader = LAZY_VIEW_LOADERS[modKey];
  if (!loader) return false;
  try {
    const factory = await loader();
    const view = factory(supa, auth);
    if (window.IFI?.studio) {
      (window.IFI.studio as Record<string, unknown>)[modKey] = view;
    }
    return true;
  } catch (e) {
    console.error(`[view-loader] failed to load ${modKey}:`, e);
    return false;
  }
}
